import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { admin, custom, departments, externalTypes, services, userTypes } from "data/authData";
import { getRoleKeys, getRoleLabel } from "./roleHelpers";
import { modalCancelBtnSx, modalInputSx, UserRoleChip, userUi } from "./userUi";

const optionList = (options) => Object.values(options || {}).filter((item) => item?.key);

const groupKeys = (options) => optionList(options).map((item) => item.key);

const selectedInGroup = (roles, options) =>
	groupKeys(options).filter((key) => Boolean(roles?.[key]));

const replaceGroupRoles = (roles, options, selectedKeys) => {
	const inGroup = new Set(groupKeys(options));
	const next = {};
	Object.keys(roles || {}).forEach((key) => {
		if (!inGroup.has(key)) next[key] = roles[key];
	});
	(selectedKeys || []).forEach((key) => {
		if (key) next[key] = true;
	});
	return next;
};

const RoleSelect = ({ options, onSelect, placeholder = "Add role", disabled }) => (
	<Select
		displayEmpty
		value=""
		size="small"
		fullWidth
		disabled={disabled}
		onChange={(e) => {
			const value = e.target.value;
			if (value) onSelect(value);
		}}
		MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
		sx={modalInputSx}
		renderValue={() => <Typography sx={{ color: userUi.textMuted, fontSize: 14 }}>{placeholder}</Typography>}>
		<MenuItem value="" sx={{ display: "none" }} />
		{optionList(options).map((roleData) => (
			<MenuItem key={roleData.key} value={roleData.key}>
				{roleData.name}
			</MenuItem>
		))}
	</Select>
);

const RoleMultiSelect = ({ options, value, onChange, placeholder = "Select", disabled }) => {
	const selected = Array.isArray(value) ? value : [];

	return (
		<Select
			multiple
			displayEmpty
			value={selected}
			size="small"
			fullWidth
			disabled={disabled}
			onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
			MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
			sx={modalInputSx}
			renderValue={(selectedKeys) => {
				if (!selectedKeys?.length) {
					return <Typography sx={{ color: userUi.textMuted, fontSize: 14 }}>{placeholder}</Typography>;
				}
				if (selectedKeys.length <= 2) {
					return (
						<Typography noWrap sx={{ fontSize: 14 }}>
							{selectedKeys.map(getRoleLabel).join(", ")}
						</Typography>
					);
				}
				return (
					<Typography noWrap sx={{ fontSize: 14 }}>
						{selectedKeys.length} selected
					</Typography>
				);
			}}>
			{optionList(options).map((roleData) => (
				<MenuItem key={roleData.key} value={roleData.key} sx={{ py: 0.25 }}>
					<Checkbox size="small" checked={selected.includes(roleData.key)} />
					<ListItemText primary={roleData.name} primaryTypographyProps={{ fontSize: 14 }} />
				</MenuItem>
			))}
		</Select>
	);
};

export const RoleChips = ({ role, max, onDelete, emptyLabel = "No access assigned", nowrap = false }) => {
	const keys = getRoleKeys(role);
	const shown = typeof max === "number" ? keys.slice(0, max) : keys;
	const rest = typeof max === "number" ? keys.slice(max) : [];

	if (!keys.length) {
		return (
			<Typography noWrap sx={{ fontSize: 13, color: userUi.textMuted }}>
				{emptyLabel}
			</Typography>
		);
	}

	return (
		<Box
			display="flex"
			gap={0.75}
			flexWrap={nowrap ? "nowrap" : "wrap"}
			alignItems="center"
			minWidth={0}
			overflow={nowrap ? "hidden" : "visible"}>
			{shown.map((key) => (
				<UserRoleChip
					key={key}
					label={getRoleLabel(key)}
					isAdmin={key === admin}
					onDelete={onDelete ? () => onDelete(key) : undefined}
				/>
			))}
			{rest.length > 0 ? (
				<Tooltip title={rest.map(getRoleLabel).join(", ")}>
					<Box sx={{ flexShrink: 0 }}>
						<UserRoleChip label={`+${rest.length}`} />
					</Box>
				</Tooltip>
			) : null}
		</Box>
	);
};

const RoleAccessSection = ({ roles, onRolesChange, onAdd, onAddAdmin, userType, readOnly }) => {
	const isExternal = userType === userTypes.external;

	const handleGroupChange = (options) => (selectedKeys) => {
		onRolesChange(replaceGroupRoles(roles, options, selectedKeys));
	};

	const handleChipDelete = (roleToDelete) => {
		const next = { ...(roles || {}) };
		delete next[roleToDelete];
		onRolesChange(next);
	};

	return (
		<Box
			sx={{
				width: "100%",
				border: `1px solid ${userUi.border}`,
				borderRadius: 1.5,
				bgcolor: "#FAFAFA",
				p: { xs: 1.5, md: 2 },
			}}>
			<Typography sx={{ fontWeight: 600, fontSize: 15, color: userUi.textPrimary, mb: 1.25 }}>
				User access
			</Typography>
			<Box sx={{ mb: 1.5 }}>
				<RoleChips role={roles} onDelete={readOnly ? undefined : handleChipDelete} />
			</Box>
			{readOnly ? null : isExternal ? (
				<Box sx={{ maxWidth: 280 }}>
					<Typography sx={{ fontSize: 13, fontWeight: 500, color: userUi.textSecondary, mb: 0.75 }}>
						External type
					</Typography>
					<RoleSelect options={externalTypes} onSelect={onAdd} placeholder="Add external type" />
				</Box>
			) : (
				<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
					<Box sx={{ flex: 1, minWidth: 200 }}>
						<Typography sx={{ fontSize: 13, fontWeight: 500, color: userUi.textSecondary, mb: 0.75 }}>
							Department
						</Typography>
						<RoleMultiSelect
							options={departments}
							value={selectedInGroup(roles, departments)}
							onChange={handleGroupChange(departments)}
							placeholder="Select departments"
						/>
					</Box>
					<Box sx={{ flex: 1, minWidth: 200 }}>
						<Typography sx={{ fontSize: 13, fontWeight: 500, color: userUi.textSecondary, mb: 0.75 }}>
							Service
						</Typography>
						<RoleMultiSelect
							options={services}
							value={selectedInGroup(roles, services)}
							onChange={handleGroupChange(services)}
							placeholder="Select services"
						/>
					</Box>
					<Box sx={{ flex: 1, minWidth: 200 }}>
						<Typography sx={{ fontSize: 13, fontWeight: 500, color: userUi.textSecondary, mb: 0.75 }}>
							Custom tag
						</Typography>
						<RoleMultiSelect
							options={custom}
							value={selectedInGroup(roles, custom)}
							onChange={handleGroupChange(custom)}
							placeholder="Select custom tags"
						/>
					</Box>
					<Box sx={{ display: "flex", alignItems: "flex-end" }}>
						<Button
							variant="outlined"
							onClick={onAddAdmin}
							sx={{ ...modalCancelBtnSx, height: 40, whiteSpace: "nowrap" }}>
							Add admin
						</Button>
					</Box>
				</Stack>
			)}
		</Box>
	);
};

export default RoleAccessSection;
